import React, { useState, useEffect } from 'react';
import { usersAPI, userGroupAPI, authAPI, customerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const UserManagement = () => {
  const { isSuperAdmin, customerId } = useAuth();
  const { groups } = useGroup();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignFormData, setAssignFormData] = useState({
    user_id: '',
    group_id: '',
    role: 'user'
  });
  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    customer_id: null,
    is_super_admin: false,
    auto_assign_group: false,
    auto_assign_group_id: '',
    auto_assign_role: 'user'
  });
  const [filterGroupId, setFilterGroupId] = useState('');
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    userId: null,
    userName: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadUsers();
    if (isSuperAdmin) {
      loadCustomers();
    }
  }, [isSuperAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userGroupAPI.getAllUsersWithGroups();
      setUsers(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await userGroupAPI.assignUserToGroup(assignFormData);
      setSuccess('User assigned to group successfully');
      await loadUsers();
      setShowAssignForm(false);
      setAssignFormData({ user_id: '', group_id: '', role: 'user' });
      setSelectedUser(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign user to group');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userGroupId, newRole) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupAPI.updateUserGroupRole(userGroupId, { role: newRole });
      setSuccess('User role updated successfully');
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromGroup = async (userGroupId) => {
    if (!window.confirm('Are you sure you want to remove this user from the group?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await userGroupAPI.removeUserFromGroup(userGroupId);
      setSuccess('User removed from group successfully');
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user from group');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Prepare user data
      const userData = {
        email: createFormData.email,
        password: createFormData.password,
        name: createFormData.name,
        role: createFormData.role
      };

      // Only include customer_id and is_super_admin if super admin
      if (isSuperAdmin) {
        if (createFormData.customer_id) {
          userData.customer_id = createFormData.customer_id;
        }
        userData.is_super_admin = createFormData.is_super_admin;
      } else if (customerId) {
        // Non-super admin users get their customer_id from context
        userData.customer_id = customerId;
      }

      const response = await authAPI.register(userData);
      const newUser = response.data.data;
      setSuccess('User created successfully');
      
      // Optionally assign to default group if group_id is provided
      if (createFormData.auto_assign_group && createFormData.auto_assign_group_id) {
        try {
          await userGroupAPI.assignUserToGroup({
            user_id: newUser.id,
            group_id: createFormData.auto_assign_group_id,
            role: createFormData.auto_assign_role || 'user'
          });
          setSuccess('User created and assigned to group successfully');
        } catch (err) {
          console.error('Failed to auto-assign to group:', err);
          setSuccess('User created successfully (group assignment failed)');
        }
      }
      
      await loadUsers();
      setShowCreateForm(false);
      setCreateFormData({
        email: '',
        password: '',
        name: '',
        role: 'user',
        customer_id: null,
        is_super_admin: false,
        auto_assign_group: false,
        auto_assign_group_id: '',
        auto_assign_role: 'user'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (user) => {
    try {
      setLoading(true);
      setError(null);
      const updateData = {
        name: user.name,
        role: user.role,
        is_active: user.is_active
      };
      if (isSuperAdmin && user.customer_id) {
        updateData.customer_id = user.customer_id;
      }
      await usersAPI.update(user.id, updateData);
      setSuccess('User updated successfully');
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await usersAPI.remove(userId);
      setSuccess('User deleted successfully');
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    if (resetPasswordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await usersAPI.resetPassword(resetPasswordData.userId, resetPasswordData.newPassword);
      setSuccess('Password reset successfully');
      setShowResetPasswordForm(false);
      setResetPasswordData({
        userId: null,
        userName: '',
        newPassword: '',
        confirmPassword: ''
      });
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Filter users by group
  const filteredUsers = filterGroupId
    ? users.filter(user => 
        user.userGroups?.some(ug => ug.group_id === parseInt(filterGroupId))
      )
    : users;

  if (loading && users.length === 0) {
    return <LoadingSpinner text="Loading users..." />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>User Management</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            + Create User
          </button>
          <button className="btn btn-outline-primary" onClick={() => setShowAssignForm(true)}>
            Assign to Group
          </button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowCreateForm(false);
            setCreateFormData({
              email: '',
              password: '',
              name: '',
              role: 'user',
              customer_id: null,
              is_super_admin: false,
              auto_assign_group: false,
              auto_assign_group_id: '',
              auto_assign_role: 'user'
            });
          }
        }}>
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Create New User</h3>
              <button 
                className="btn-close" 
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateFormData({
                    email: '',
                    password: '',
                    name: '',
                    role: 'user',
                    customer_id: null,
                    is_super_admin: false,
                    auto_assign_group: false,
                    auto_assign_group_id: '',
                    auto_assign_role: 'user'
                  });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser} style={{ padding: '1.5rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={createFormData.name}
                    onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Global Role *</label>
                  <select
                    className="form-control"
                    value={createFormData.role}
                    onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value })}
                    required
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <small className="form-text text-muted">
                    Global role applies across all groups. Group-specific roles can be assigned after creation.
                  </small>
                </div>
              </div>
              {isSuperAdmin && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Customer</label>
                      <select
                        className="form-control"
                        value={createFormData.customer_id || ''}
                        onChange={(e) => setCreateFormData({ ...createFormData, customer_id: e.target.value ? parseInt(e.target.value) : null })}
                      >
                        <option value="">Select Customer (Optional)</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name} ({customer.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <div className="form-check" style={{ marginTop: '30px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_super_admin"
                          checked={createFormData.is_super_admin}
                          onChange={(e) => setCreateFormData({ ...createFormData, is_super_admin: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="is_super_admin">
                          Super Admin (Access to all groups and customers)
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="form-row">
                <div className="form-group">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="auto_assign_group"
                      checked={createFormData.auto_assign_group}
                      onChange={(e) => setCreateFormData({ ...createFormData, auto_assign_group: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="auto_assign_group">
                      Automatically assign to group after creation
                    </label>
                  </div>
                </div>
              </div>
              {createFormData.auto_assign_group && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Assign to Group</label>
                    <select
                      className="form-control"
                      value={createFormData.auto_assign_group_id}
                      onChange={(e) => setCreateFormData({ ...createFormData, auto_assign_group_id: parseInt(e.target.value) })}
                    >
                      <option value="">Select Group</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Role in Group</label>
                    <select
                      className="form-control"
                      value={createFormData.auto_assign_role}
                      onChange={(e) => setCreateFormData({ ...createFormData, auto_assign_role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateForm(false);
                    setCreateFormData({
                      email: '',
                      password: '',
                      name: '',
                      role: 'user',
                      customer_id: null,
                      is_super_admin: false,
                      auto_assign_group: false,
                      auto_assign_group_id: '',
                      auto_assign_role: 'user'
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter by Group */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <label style={{ fontWeight: '500' }}>Filter by Group:</label>
        <select
          className="form-control"
          style={{ width: '250px' }}
          value={filterGroupId}
          onChange={(e) => setFilterGroupId(e.target.value)}
        >
          <option value="">All Groups</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {/* Reset Password Modal */}
      {showResetPasswordForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowResetPasswordForm(false);
            setResetPasswordData({
              userId: null,
              userName: '',
              newPassword: '',
              confirmPassword: ''
            });
          }
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Reset Password for {resetPasswordData.userName}</h3>
              <button 
                className="btn-close" 
                onClick={() => {
                  setShowResetPasswordForm(false);
                  setResetPasswordData({
                    userId: null,
                    userName: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleResetPassword} style={{ padding: '1.5rem' }}>
              {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  className="form-control"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                />
                <small className="form-text text-muted">
                  Password must be at least 6 characters long
                </small>
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  className="form-control"
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  required
                  minLength={6}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowResetPasswordForm(false);
                    setResetPasswordData({
                      userId: null,
                      userName: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign User to Group Modal */}
      {showAssignForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowAssignForm(false);
            setAssignFormData({ user_id: '', group_id: '', role: 'user' });
            setSelectedUser(null);
          }
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign User to Group</h3>
              <button 
                className="btn-close" 
                onClick={() => {
                  setShowAssignForm(false);
                  setAssignFormData({ user_id: '', group_id: '', role: 'user' });
                  setSelectedUser(null);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAssignUser} style={{ padding: '1.5rem' }}>
              <div className="form-group">
                <label>User *</label>
                <select
                  className="form-control"
                  value={assignFormData.user_id}
                  onChange={(e) => setAssignFormData({ ...assignFormData, user_id: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} {user.is_super_admin ? '(Super Admin)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Group *</label>
                <select
                  className="form-control"
                  value={assignFormData.group_id}
                  onChange={(e) => setAssignFormData({ ...assignFormData, group_id: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select Group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Role in Group *</label>
                <select
                  className="form-control"
                  value={assignFormData.role}
                  onChange={(e) => setAssignFormData({ ...assignFormData, role: e.target.value })}
                  required
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <small className="form-text text-muted">
                  Admin: Full access to group. Manager: Can manage employees/documents. User: View only.
                </small>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignForm(false);
                    setAssignFormData({ user_id: '', group_id: '', role: 'user' });
                    setSelectedUser(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Assigning...' : 'Assign to Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h3>Users and Group Assignments</h3>
        </div>
        <div className="card-body">
          {filteredUsers.length === 0 ? (
            <p>No users found.</p>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Global Role</th>
                    <th>Groups</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.name || 'N/A'}</strong>
                        {user.is_super_admin && (
                          <span className="badge badge-warning" style={{ marginLeft: '8px' }}>
                            Super Admin
                          </span>
                        )}
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${user.role === 'admin' ? 'badge-primary' : 'badge-secondary'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        {user.userGroups && user.userGroups.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {user.userGroups.map((ug) => (
                              <div
                                key={ug.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '4px 8px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px',
                                  marginBottom: '4px'
                                }}
                              >
                                <span style={{ fontWeight: '500' }}>
                                  {ug.group?.name || `Group ${ug.group_id}`}
                                </span>
                                <select
                                  className="form-control form-control-sm"
                                  style={{ width: 'auto', display: 'inline-block', padding: '2px 6px' }}
                                  value={ug.role}
                                  onChange={(e) => handleUpdateRole(ug.id, e.target.value)}
                                >
                                  <option value="user">User</option>
                                  <option value="manager">Manager</option>
                                  <option value="admin">Admin</option>
                                </select>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveFromGroup(ug.id)}
                                  title="Remove from group"
                                >
                                  <i class="fa-solid fa-user-minus"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>No groups assigned</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group" style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setSelectedUser(user);
                              setAssignFormData({
                                user_id: user.id,
                                group_id: '',
                                role: 'user'
                              });
                              setShowAssignForm(true);
                            }}
                            title="Add to Group"
                          >
                           <i class="fa-solid fa-plus"></i>Group
                          </button>
                          {isSuperAdmin && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => {
                                setResetPasswordData({
                                  userId: user.id,
                                  userName: user.name || user.email,
                                  newPassword: '',
                                  confirmPassword: ''
                                });
                                setShowResetPasswordForm(true);
                              }}
                              title="Reset Password"
                            >
                             <i class="fa-solid fa-key"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              const newName = window.prompt('Enter new name:', user.name || '');
                              if (newName !== null) {
                                handleEditUser({ ...user, name: newName });
                              }
                            }}
                            title="Edit User"
                          >
                              <i class="fa-solid fa-pen-to-square"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete User"
                          >
                           <i class="fa-solid fa-trash-can"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;

